import { Box, Card, CardContent, Typography, Button, List, ListItem, ListItemIcon, ListItemText, Stack, Chip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Shared styles
const cardBaseStyles = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 3,
  transition: 'all 0.3s ease',
};

const freeCardStyles = {
  ...cardBaseStyles,
  background: 'linear-gradient(135deg, rgba(44, 48, 52, 0.9) 0%, rgba(33, 37, 41, 0.95) 100%)',
  border: '1px solid rgba(144, 202, 249, 0.2)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(144, 202, 249, 0.15)',
  },
};

const premiumCardStyles = {
  ...cardBaseStyles,
  background: `
    linear-gradient(135deg, rgba(144, 202, 249, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%),
    linear-gradient(135deg, rgba(44, 48, 52, 0.95) 0%, rgba(33, 37, 41, 1) 100%)
  `,
  border: '2px solid rgba(144, 202, 249, 0.4)',
  position: 'relative',
  overflow: 'visible',
  boxShadow: '0 16px 40px rgba(144, 202, 249, 0.12)',
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 50px rgba(144, 202, 249, 0.18)',
  },
};

const buttonStyles = {
  py: 1.5,
  borderWidth: 2,
  borderColor: '#90caf9',
  color: '#90caf9',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  borderRadius: 2,
  background: 'rgba(144, 202, 249, 0.05)',
  backdropFilter: 'blur(8px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#42a5f5',
    borderWidth: 2,
    background: 'rgba(144, 202, 249, 0.12)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(144, 202, 249, 0.2)',
    '& .MuiButton-endIcon': {
      transform: 'translateX(4px)',
    },
  },
  '& .MuiButton-endIcon': {
    transition: 'transform 0.3s ease',
  },
};

const premiumButtonStyles = {
  ...buttonStyles,
  background: 'rgba(144, 202, 249, 0.08)',
  '&:hover': {
    ...buttonStyles['&:hover'],
    background: 'rgba(144, 202, 249, 0.15)',
    boxShadow: '0 12px 32px rgba(144, 202, 249, 0.4)',
  },
};

/**
 * Reusable plan card component for pricing displays
 *
 * @param {Object} props
 * @param {'free' | 'premium'} props.variant - Card variant
 * @param {string} props.name - Plan name
 * @param {string} props.price - Price display (e.g., "0 kr" or "399 kr")
 * @param {string} [props.pricePeriod] - Period suffix (e.g., "/year")
 * @param {string} [props.subtitle] - Subtitle under price
 * @param {Array<string|{text: string, highlight?: boolean}>} props.features - Feature list
 * @param {string} props.buttonText - CTA button text
 * @param {Function} props.onButtonClick - Button click handler
 * @param {boolean} [props.loading] - Show loading spinner on button
 * @param {string} [props.badge] - Badge text (typically for premium)
 * @param {'top' | 'bottom'} [props.buttonPosition='bottom'] - Button placement
 * @param {boolean} [props.compactFeatures] - Use smaller feature text
 * @param {boolean} [props.selected] - Show selected state (for wizard/selection UI)
 * @param {Function} [props.onCardClick] - Card click handler (enables selection mode)
 */
function PlanCard({
  variant = 'free',
  name,
  price,
  pricePeriod,
  subtitle,
  features = [],
  buttonText,
  onButtonClick,
  loading = false,
  badge,
  buttonPosition = 'bottom',
  compactFeatures = false,
  selected = false,
  onCardClick,
}) {
  const isPremium = variant === 'premium';
  const isSelectable = Boolean(onCardClick);
  const baseCardStyles = isPremium ? premiumCardStyles : freeCardStyles;

  // Apply selection styling when in selection mode
  const cardStyles = isSelectable
    ? {
        ...baseCardStyles,
        cursor: 'pointer',
        border: selected ? '2px solid #90caf9' : baseCardStyles.border,
        boxShadow: selected ? '0 0 20px rgba(144, 202, 249, 0.3)' : baseCardStyles.boxShadow,
      }
    : baseCardStyles;

  const btnStyles = isPremium ? premiumButtonStyles : buttonStyles;
  const checkColor = isPremium ? '#90caf9' : '#66bb6a';

  const handleButtonClick = (e) => {
    if (isSelectable) {
      e.stopPropagation(); // Prevent card click when button is clicked
    }
    onButtonClick?.(e);
  };

  const renderButton = () => (
    <Button
      variant="outlined"
      fullWidth
      size="large"
      onClick={handleButtonClick}
      disabled={loading}
      endIcon={loading ? null : <ArrowForwardIcon />}
      sx={{
        ...btnStyles,
        mb: buttonPosition === 'top' ? 4 : 0,
      }}
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : buttonText}
    </Button>
  );

  const renderFeatures = () => (
    <List
      dense={compactFeatures}
      sx={{
        mb: buttonPosition === 'bottom' ? 3 : 0,
        '& .MuiListItem-root': compactFeatures ? undefined : { px: 0, py: 0.75 },
      }}
    >
      {features.map((feature, index) => {
        const isObject = typeof feature === 'object';
        const text = isObject ? feature.text : feature;
        const highlight = isObject ? feature.highlight : false;

        return (
          <ListItem key={index} sx={compactFeatures ? { px: 0, py: 0.5 } : undefined}>
            <ListItemIcon sx={{ minWidth: compactFeatures ? 28 : 32 }}>
              <CheckCircleIcon sx={{ fontSize: compactFeatures ? 18 : 20, color: highlight ? '#90caf9' : checkColor }} />
            </ListItemIcon>
            <ListItemText
              primary={text}
              primaryTypographyProps={{
                variant: 'body2',
                sx: {
                  color: highlight ? '#90caf9' : '#DEE2E6',
                  fontSize: compactFeatures ? '0.85rem' : '0.9rem',
                  fontWeight: highlight ? 600 : 400,
                }
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Card elevation={0} sx={cardStyles} onClick={onCardClick}>
      {/* Badge for premium */}
      {badge && (
        <Chip
          label={badge}
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#90caf9',
            color: '#212529',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.5px',
            height: 28,
            zIndex: 1,
          }}
        />
      )}

      <CardContent
        sx={{
          p: 4,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ minHeight: { md: 140 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#FFFFFF',
            }}
          >
            {name}
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: subtitle ? 2 : 3 }}>
            <Typography
              variant={subtitle ? 'h3' : 'h4'}
              sx={{
                fontWeight: 800,
                color: '#90caf9',
              }}
            >
              {price}
            </Typography>
            {pricePeriod && (
              <Typography variant="h6" sx={{ color: '#ADB5BD' }}>
                {pricePeriod}
              </Typography>
            )}
          </Stack>

          {subtitle && (
            <Typography variant="body2" sx={{ color: '#ADB5BD' }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Button at top */}
        {buttonPosition === 'top' && renderButton()}

        {/* Features - grows to push button to bottom on desktop */}
        <Box sx={{ flexGrow: { xs: 0, md: 1 }, display: 'flex', flexDirection: 'column' }}>
          {renderFeatures()}
        </Box>

        {/* Button at bottom */}
        {buttonPosition === 'bottom' && renderButton()}
      </CardContent>
    </Card>
  );
}

export default PlanCard;
